import React, { useEffect, useMemo, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Wrench, Plus, Loader2, MapPin, Clock, User, Hash, AlertTriangle,
  Sparkles, Activity, Boxes, Truck, Check, X, Trophy,
} from "lucide-react";
import { toast } from "sonner";
import { propriedadeLabel } from "../lib/labels";
import { DeleteButton } from "../components/DeleteButton";

const COLUNAS = [
  { id: "aguardando_triagem", label: "AGUARDANDO TRIAGEM", cor: "bg-zinc-100", accent: "border-zinc-300" },
  { id: "aguardando_peca",   label: "AGUARDANDO PEÇA",   cor: "bg-amber-50",  accent: "border-amber-300" },
  { id: "em_manutencao",     label: "EM MANUTENÇÃO",      cor: "bg-blue-50",   accent: "border-blue-300" },
  { id: "pintura",           label: "PINTURA",            cor: "bg-fuchsia-50",accent: "border-fuchsia-300" },
  { id: "solda",             label: "SOLDA",              cor: "bg-orange-50", accent: "border-orange-300" },
  { id: "teste_qualidade",   label: "TESTE/QUALIDADE",    cor: "bg-purple-50", accent: "border-purple-300" },
  { id: "pronto_para_rota",  label: "PRONTO PARA ROTA",   cor: "bg-emerald-50",accent: "border-emerald-300" },
  { id: "finalizado",        label: "FINALIZADO",         cor: "bg-green-50",  accent: "border-green-400" },
  { id: "irrecuperavel",     label: "IRRECUPERÁVEL",      cor: "bg-red-50",    accent: "border-red-400" },
];

const PRIORIDADE_BADGE = {
  baixa: "bg-zinc-100 text-zinc-700",
  normal: "bg-blue-100 text-blue-700",
  urgente: "bg-amber-100 text-amber-700",
  critica: "bg-red-100 text-red-700 animate-pulse",
};
const TIPO_ENTRADA_LABEL = {
  troca: "TROCA", devolucao: "DEVOLUÇÃO", manutencao: "MANUTENÇÃO",
  aditivo: "ADITIVO", preventiva: "PREVENTIVA",
};

const fmt = (n) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n || 0);

function cardColor(job) {
  if (job.coluna === "aguardando_peca") return "border-l-amber-500";
  if (job.prioridade === "critica") return "border-l-red-600";
  if (job.prioridade === "urgente") return "border-l-orange-500";
  if (job.idade_dias > 14) return "border-l-purple-500";
  if (job.idade_dias > 7) return "border-l-yellow-500";
  return "border-l-zinc-300";
}

// ============================================================
function JobCard({ job, onClick }) {
  return (
    <div
      onClick={() => onClick(job)}
      className={`p-2.5 rounded-md bg-white border border-zinc-200 border-l-4 ${cardColor(job)} hover:shadow-md transition cursor-pointer space-y-1.5`}
      data-testid={`job-card-${job.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-xs font-semibold text-zinc-700 truncate">
          {job.equipamento?.serial || job.equipamento_id}
        </span>
        <Badge className={`${PRIORIDADE_BADGE[job.prioridade] || "bg-zinc-100"} font-mono text-[9px] px-1.5`}>
          {String(job.prioridade || "normal").toUpperCase()}
        </Badge>
      </div>
      <div className="text-[11px] text-zinc-600 truncate">
        {job.equipamento?.placa && <span className="font-mono">{job.equipamento.placa} · </span>}
        {propriedadeLabel(job.equipamento?.propriedade)}
      </div>
      {job.cliente_origem && (
        <div className="text-[11px] text-zinc-500 truncate flex items-center gap-1">
          <MapPin className="w-3 h-3 shrink-0" />
          <span className="truncate">{job.cliente_origem.nome_fantasia} · {job.cliente_origem.uf}</span>
        </div>
      )}
      <div className="flex items-center justify-between text-[10px] text-zinc-500 mt-1">
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{job.idade_dias}d</span>
        <span>{TIPO_ENTRADA_LABEL[job.tipo_entrada] || job.tipo_entrada}</span>
      </div>
      {(job.custo_acumulado || 0) > 0 && (
        <div className="text-[10px] text-emerald-700 font-mono text-right">
          {fmt(job.custo_acumulado)}
        </div>
      )}
      {job.mecanico && (
        <div className="text-[10px] text-zinc-500 truncate flex items-center gap-1">
          <User className="w-3 h-3 shrink-0" />{job.mecanico.name}
        </div>
      )}
    </div>
  );
}

// ============================================================
function ColunaKpi({ titulo, count, custo, tempo }) {
  return (
    <div className="px-2 py-1.5 bg-white/70 rounded text-[10px] text-zinc-600 flex items-center justify-between mb-2">
      <span className="font-semibold text-zinc-700">{titulo}</span>
      <div className="flex items-center gap-2">
        <span>{count} eq.</span>
        {custo > 0 && <span className="text-emerald-700">{fmt(custo)}</span>}
        {tempo > 0 && <span className="text-zinc-400">⌀ {tempo}d</span>}
      </div>
    </div>
  );
}

// ============================================================
function ModalEnviar({ open, onClose, onSent }) {
  const [search, setSearch] = useState("");
  const [opts, setOpts] = useState([]);
  const [eq, setEq] = useState(null);
  const [form, setForm] = useState({
    tipo_entrada: "manutencao", prioridade: "normal",
    motivo: "", observacoes: "", tipo_servico: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!search.trim()) { setOpts([]); return; }
    const t = setTimeout(() => {
      api.get("/inventario/equipamentos", { params: { q: search, limit: 8 } })
        .then((r) => setOpts(r.data.items || []));
    }, 200);
    return () => clearTimeout(t);
  }, [search]);

  const enviar = async () => {
    if (!eq) return toast.error("Selecione um equipamento");
    setBusy(true);
    try {
      const { data } = await api.post("/oficina/enviar", {
        equipamento_id: eq.id, ...form,
      });
      toast.success(`${eq.serial} enviado para oficina`);
      onSent(data.job);
      onClose();
      setEq(null); setSearch(""); setOpts([]);
      setForm({ tipo_entrada: "manutencao", prioridade: "normal", motivo: "", observacoes: "", tipo_servico: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="modal-enviar-oficina">
        <DialogHeader>
          <DialogTitle>Enviar para Oficina</DialogTitle>
          <DialogDescription>Triagem inicial será gerada por IA automaticamente.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!eq ? (
            <div>
              <Label className="text-xs uppercase tracking-wide">Equipamento</Label>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar serial ou placa..." data-testid="enviar-search" />
              {opts.length > 0 && (
                <div className="border rounded mt-1 max-h-44 overflow-auto">
                  {opts.map((o) => (
                    <button key={o.id} onClick={() => { setEq(o); setOpts([]); }}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-zinc-50 border-b last:border-0"
                      data-testid={`enviar-opt-${o.id}`}>
                      <span className="font-mono text-xs">{o.serial}</span>
                      <span className="text-xs text-zinc-500 ml-2">{o.placa || "—"} · {propriedadeLabel(o.propriedade)} · {o.status}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="p-2 border rounded bg-zinc-50 text-sm flex items-center justify-between">
              <div>
                <div className="font-mono text-xs">{eq.serial} · {eq.placa || "—"}</div>
                <div className="text-xs text-zinc-500">{propriedadeLabel(eq.propriedade)} · status atual: {eq.status}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setEq(null)}>Trocar</Button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">Tipo</Label>
              <Select value={form.tipo_entrada} onValueChange={(v) => setForm({...form, tipo_entrada: v})}>
                <SelectTrigger data-testid="enviar-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manutencao">MANUTENÇÃO</SelectItem>
                  <SelectItem value="troca">TROCA</SelectItem>
                  <SelectItem value="devolucao">DEVOLUÇÃO</SelectItem>
                  <SelectItem value="aditivo">ADITIVO</SelectItem>
                  <SelectItem value="preventiva">PREVENTIVA</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Prioridade</Label>
              <Select value={form.prioridade} onValueChange={(v) => setForm({...form, prioridade: v})}>
                <SelectTrigger data-testid="enviar-prioridade"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="baixa">BAIXA</SelectItem>
                  <SelectItem value="normal">NORMAL</SelectItem>
                  <SelectItem value="urgente">URGENTE</SelectItem>
                  <SelectItem value="critica">CRÍTICA</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Motivo</Label>
            <Input value={form.motivo} onChange={(e) => setForm({...form, motivo: e.target.value})} placeholder="Ex: vazamento hidráulico" data-testid="enviar-motivo" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Tipo de serviço</Label>
            <Input value={form.tipo_servico} onChange={(e) => setForm({...form, tipo_servico: e.target.value})} placeholder="Ex: mecânica geral" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Observações</Label>
            <Textarea value={form.observacoes} onChange={(e) => setForm({...form, observacoes: e.target.value})} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={enviar} disabled={!eq || busy} data-testid="enviar-confirmar">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wrench className="w-4 h-4 mr-2" />}
            Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
function DrawerDetalhe({ jobId, open, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [pecas, setPecas] = useState([]);
  const [mecanicos, setMecanicos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pecaSel, setPecaSel] = useState("");
  const [qtd, setQtd] = useState(1);
  const [busy, setBusy] = useState(false);

  const carregar = async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const [d, p, m] = await Promise.all([
        api.get(`/oficina/${jobId}/detalhe`),
        api.get("/oficina/pecas/estoque"),
        api.get("/oficina/mecanicos"),
      ]);
      setData(d.data); setPecas(p.data.items || []); setMecanicos(m.data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) carregar(); }, [open, jobId]);

  const aplicarPeca = async () => {
    if (!pecaSel) return toast.error("Selecione peça");
    setBusy(true);
    try {
      await api.post(`/oficina/${jobId}/pecas`, { peca_id: pecaSel, quantidade: parseInt(qtd) || 1 });
      toast.success("Peça aplicada");
      await carregar();
      onChanged?.();
      setQtd(1); setPecaSel("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setBusy(false); }
  };

  const setMecanico = async (mid) => {
    try {
      await api.patch(`/oficina/${jobId}/status`, { nova_coluna: data.job.coluna, mecanico_id: mid });
      toast.success("Mecânico atribuído");
      await carregar();
      onChanged?.();
    } catch (e) {
      toast.error("Erro");
    }
  };

  const criarOS = async () => {
    try {
      await api.post(`/oficina/${jobId}/os`, {
        mecanico_id: data.job.mecanico_id,
        checklist: ["Inspeção visual", "Teste funcional", "Limpeza"],
      });
      toast.success("OS criada");
      await carregar();
    } catch (e) { toast.error("Erro"); }
  };

  if (!data) return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
        </div>
      </SheetContent>
    </Sheet>
  );

  const job = data.job;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="drawer-detalhe-oficina">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-orange-600" />
            <span className="font-mono">{job.equipamento?.serial}</span>
            <Badge className={`${PRIORIDADE_BADGE[job.prioridade]} font-mono text-[10px]`}>
              {String(job.prioridade).toUpperCase()}
            </Badge>
            <div className="ml-auto">
              <DeleteButton
                url={`/oficina/jobs/${job.id}`}
                label={`OS oficina · ${job.equipamento?.serial || job.id.slice(0, 8)}`}
                onDeleted={() => { onClose(); onChanged?.(); }}
                testId={`del-job-${job.id}`}
              />
            </div>
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* IA */}
          {job.triagem_ia && (
            <Card className="p-3 border-purple-200 bg-purple-50">
              <h4 className="text-xs uppercase tracking-wide text-purple-700 font-semibold mb-1.5 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> Análise IA
              </h4>
              <p className="text-xs text-zinc-700 whitespace-pre-line">{job.triagem_ia}</p>
            </Card>
          )}

          {/* Dados */}
          <Card className="p-3">
            <h4 className="text-xs uppercase tracking-wide text-zinc-500 font-semibold mb-2">Dados</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-zinc-400">Tipo entrada:</span> <strong>{TIPO_ENTRADA_LABEL[job.tipo_entrada]}</strong></div>
              <div><span className="text-zinc-400">Idade:</span> <strong>{job.idade_dias}d</strong></div>
              <div><span className="text-zinc-400">Cliente origem:</span> <strong>{job.cliente_origem?.nome_fantasia || "—"}</strong></div>
              <div><span className="text-zinc-400">Cidade/UF:</span> <strong>{job.cliente_origem?.cidade || "—"}/{job.cliente_origem?.uf || "—"}</strong></div>
              <div><span className="text-zinc-400">Coluna:</span> <strong>{job.coluna}</strong></div>
              <div><span className="text-zinc-400">Custo:</span> <strong className="text-emerald-700">{fmt(job.custo_acumulado)}</strong></div>
              {job.motivo && <div className="col-span-2"><span className="text-zinc-400">Motivo:</span> {job.motivo}</div>}
              {job.observacoes && <div className="col-span-2"><span className="text-zinc-400">Obs:</span> {job.observacoes}</div>}
            </div>
          </Card>

          {/* Mecânico */}
          <Card className="p-3">
            <h4 className="text-xs uppercase tracking-wide text-zinc-500 font-semibold mb-2">Mecânico responsável</h4>
            <Select value={job.mecanico_id || ""} onValueChange={setMecanico}>
              <SelectTrigger data-testid="set-mecanico"><SelectValue placeholder="Atribuir mecânico..." /></SelectTrigger>
              <SelectContent>
                {mecanicos.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} ({m.role})</SelectItem>)}
              </SelectContent>
            </Select>
          </Card>

          {/* Peças aplicadas */}
          <Card className="p-3" data-testid="card-pecas">
            <h4 className="text-xs uppercase tracking-wide text-zinc-500 font-semibold mb-2 flex items-center justify-between">
              <span>Peças aplicadas ({(job.pecas || []).length})</span>
            </h4>
            {(job.pecas || []).length > 0 && (
              <div className="space-y-1 mb-3 max-h-40 overflow-auto">
                {job.pecas.map((p) => (
                  <div key={p.id} className="text-xs flex justify-between bg-zinc-50 px-2 py-1 rounded">
                    <span><span className="font-mono text-[10px]">{p.codigo}</span> · {p.peca_nome} · {p.quantidade}x</span>
                    <span className="font-mono text-emerald-700">{fmt(p.valor_total)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Select value={pecaSel} onValueChange={setPecaSel}>
                <SelectTrigger className="flex-1" data-testid="select-peca"><SelectValue placeholder="Peça..." /></SelectTrigger>
                <SelectContent>
                  {pecas.map((p) => (
                    <SelectItem key={p.id} value={p.id} disabled={p.estoque_atual === 0}>
                      {p.codigo} · {p.nome} (est. {p.estoque_atual})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input type="number" min={1} value={qtd} onChange={(e) => setQtd(e.target.value)} className="w-20" data-testid="peca-qtd" />
              <Button size="sm" onClick={aplicarPeca} disabled={busy || !pecaSel} data-testid="aplicar-peca">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </Button>
            </div>
          </Card>

          {/* OS */}
          <Card className="p-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">Ordens de serviço ({(data.ordens || []).length})</h4>
              <Button size="sm" variant="outline" onClick={criarOS} data-testid="criar-os"><Plus className="w-3 h-3 mr-1" /> Nova OS</Button>
            </div>
            {(data.ordens || []).map((o) => (
              <div key={o.id} className="text-xs border rounded p-2 mb-1">
                <div className="flex justify-between">
                  <span className="font-mono">{o.id}</span>
                  <Badge className="font-mono text-[9px]">{o.status?.toUpperCase()}</Badge>
                </div>
                {o.tempo_execucao_min > 0 && <div className="text-zinc-500 mt-1">{o.tempo_execucao_min} min · {(o.checklist || []).length} itens</div>}
              </div>
            ))}
          </Card>

          {/* Histórico curto */}
          <Card className="p-3">
            <h4 className="text-xs uppercase tracking-wide text-zinc-500 font-semibold mb-2">Histórico ({data.historico?.length || 0})</h4>
            <div className="space-y-1 max-h-40 overflow-auto text-xs">
              {(data.historico || []).slice(0, 8).map((h, i) => (
                <div key={i} className="flex gap-2">
                  <span className="text-zinc-400">{new Date(h.data_movimentacao).toLocaleString("pt-BR")}</span>
                  <span>{h.status_anterior || "—"} → <strong>{h.status_novo}</strong></span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ============================================================
function RankingPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/oficina/ranking").then((r) => setItems(r.data.items || [])); }, []);
  if (!items.length) return null;
  const med = { ouro: "🥇", prata: "🥈", bronze: "🥉" };
  return (
    <Card className="p-3" data-testid="ranking-mecanicos">
      <h4 className="text-xs uppercase tracking-wide text-zinc-500 font-semibold mb-2 flex items-center gap-1">
        <Trophy className="w-3.5 h-3.5 text-yellow-600" /> Ranking de mecânicos
      </h4>
      <div className="space-y-1">
        {items.slice(0, 5).map((m, i) => (
          <div key={m.mecanico_id} className="text-xs flex justify-between border-b last:border-0 pb-1">
            <span>
              {med[m.medalha] || `${i + 1}.`} <strong>{m.name}</strong>
            </span>
            <span className="text-zinc-500">{m.finalizados} eq. · ⌀ {fmt(m.custo_medio)}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ============================================================
export default function Oficina() {
  const [kanban, setKanban] = useState({ items: {}, totais: {}, total: 0 });
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enviarOpen, setEnviarOpen] = useState(false);
  const [drawerJob, setDrawerJob] = useState(null);

  const carregar = async () => {
    setLoading(true);
    try {
      const [k, d] = await Promise.all([
        api.get("/oficina/em-andamento"),
        api.get("/oficina/dashboard"),
      ]);
      setKanban(k.data);
      setDashboard(d.data);
    } catch (e) {
      toast.error("Erro ao carregar oficina");
    } finally { setLoading(false); }
  };

  useEffect(() => { carregar(); }, []);

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    if (source.droppableId === destination.droppableId) return;

    // Optimistic update
    const items = { ...kanban.items };
    const src = [...(items[source.droppableId] || [])];
    const dst = [...(items[destination.droppableId] || [])];
    const idx = src.findIndex((j) => j.id === draggableId);
    if (idx === -1) return;
    const [job] = src.splice(idx, 1);
    job.coluna = destination.droppableId;
    dst.splice(destination.index, 0, job);
    items[source.droppableId] = src;
    items[destination.droppableId] = dst;
    setKanban({ ...kanban, items });

    try {
      await api.patch(`/oficina/${draggableId}/status`, { nova_coluna: destination.droppableId });
      toast.success(`Movido para ${COLUNAS.find((c) => c.id === destination.droppableId)?.label}`);
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao mover");
      carregar();
    }
  };

  const k = dashboard?.kpis || {};

  return (
    <div className="space-y-4" data-testid="page-oficina">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-zinc-900 flex items-center gap-2">
            <Wrench className="w-5 h-5 text-orange-600" /> OFICINA v2
          </h1>
          <p className="text-xs text-zinc-500">Kanban operacional · drag-and-drop entre colunas</p>
        </div>
        <Button onClick={() => setEnviarOpen(true)} data-testid="btn-enviar-oficina" className="bg-orange-600 hover:bg-orange-700">
          <Plus className="w-4 h-4 mr-1" /> Enviar p/ oficina
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        <Card className="p-3" data-testid="kpi-em-manutencao">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-blue-600" />
            <div>
              <p className="text-[10px] text-zinc-500 uppercase">Manutenção</p>
              <p className="text-lg font-bold">{k.em_manutencao || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-2">
            <Boxes className="w-4 h-4 text-amber-600" />
            <div>
              <p className="text-[10px] text-zinc-500 uppercase">Aguardando peça</p>
              <p className="text-lg font-bold">{k.aguardando_peca || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-emerald-600" />
            <div>
              <p className="text-[10px] text-zinc-500 uppercase">Prontos rota</p>
              <p className="text-lg font-bold">{k.pronto_para_rota || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <div>
              <p className="text-[10px] text-zinc-500 uppercase">Críticos</p>
              <p className="text-lg font-bold">{k.criticos || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-purple-600" />
            <div>
              <p className="text-[10px] text-zinc-500 uppercase">⌀ Reparo</p>
              <p className="text-lg font-bold">{k.tempo_medio_reparo_dias || 0}d</p>
            </div>
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-2">
            <Hash className="w-4 h-4 text-emerald-600" />
            <div>
              <p className="text-[10px] text-zinc-500 uppercase">Custo total</p>
              <p className="text-sm font-bold">{fmt(k.custo_total)}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Ranking */}
      <RankingPanel />

      {/* Kanban */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="overflow-x-auto pb-3" data-testid="kanban-board">
            <div className="flex gap-3 min-w-max">
              {COLUNAS.map((col) => {
                const items = kanban.items?.[col.id] || [];
                const t = kanban.totais?.[col.id] || {};
                return (
                  <div key={col.id} className={`w-72 ${col.cor} rounded-lg p-2 flex-shrink-0 border-t-2 ${col.accent}`} data-testid={`col-${col.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xs font-bold text-zinc-700 tracking-wide">{col.label}</h3>
                      <Badge className="bg-white text-zinc-700 border font-mono text-[10px]">{items.length}</Badge>
                    </div>
                    <ColunaKpi titulo="" count={t.count || 0} custo={t.custo} tempo={t.tempo_medio} />
                    <Droppable droppableId={col.id}>
                      {(provided, snap) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}
                             className={`space-y-2 min-h-[100px] ${snap.isDraggingOver ? "bg-white/50" : ""} rounded p-1`}>
                          {items.map((job, idx) => (
                            <Draggable key={job.id} draggableId={job.id} index={idx}>
                              {(p) => (
                                <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps}>
                                  <JobCard job={job} onClick={() => setDrawerJob(job.id)} />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                          {items.length === 0 && (
                            <div className="text-center text-[11px] text-zinc-400 py-4">vazio</div>
                          )}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}
            </div>
          </div>
        </DragDropContext>
      )}

      <ModalEnviar open={enviarOpen} onClose={() => setEnviarOpen(false)} onSent={() => carregar()} />
      <DrawerDetalhe jobId={drawerJob} open={!!drawerJob} onClose={() => setDrawerJob(null)} onChanged={carregar} />
    </div>
  );
}
